// components/organization/subscriptions/SubscriptionManagerModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../../utils/httpRequest";

const PLAN_LABELS = {
  free: { fa: "رایگان", en: "Free" },
  basic: { fa: "پایه", en: "Basic" },
  pro: { fa: "پیشرفته", en: "Pro" },
};

export default function SubscriptionManagerModal({
  isOpen,
  onClose,
  targetUser, // {_id, name, email}
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]); // لیست اشتراک‌های کاربر هدف
  const [cats, setCats] = useState([]); // فقط level 3 و hasSubscription=true
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    categoryId: "",
    months: 1,
    plan: "basic",
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      // لیست اشتراک‌های کاربر هدف (روت: GET /subscription/:userId)
      const sres = await httpRequest(
        "GET",
        `/subscription/${targetUser._id}`,
        {},
        { "x-access-token": user.token }
      );
      const sdata = sres?.data?.data || sres?.data || [];
      setSubs(Array.isArray(sdata) ? sdata : [sdata].filter(Boolean));

      // فقط دسته‌بندی‌های سطح ۳ که اشتراک دارند
      const cres = await httpRequest(
        "GET",
        "/category?level=3",
        {},
        { "x-access-token": user.token }
      );
      const carr = cres?.data?.data || cres?.data || [];
      setCats(carr);
    } catch {
      setSubs([]);
      setCats([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && targetUser?._id) fetchAll();
  }, [isOpen, targetUser?._id]);

  const handleGrant = async (e) => {
    e?.preventDefault?.();
    if (!form.categoryId) return;
    setCreating(true);
    try {
      // اعطا/تمدید ادمینی
      await httpRequest(
        "POST",
        "/subscription/admin/grant",
        {
          user: targetUser._id,
          categoryId: form.categoryId,
          plan: form.plan,
        },
        { "x-access-token": user.token }
      );
      await fetchAll();
      // ریست فرم
      setForm({ categoryId: "", months: 1, plan: "basic" });
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (sub) => {
    // نکته: برای لغو توسط ادمین بهتره روت جدا داشته باشی: POST /subscription/admin/cancel
    // اگر هنوز نساختی، موقتا می‌تونی از همین استفاده کنی بعد روت ادمینی رو اضافه کن.
    try {
      await httpRequest(
        "POST",
        "/subscription/cancel",
        {
          user: targetUser._id,
          categoryId: sub.category?._id || sub.category,
          immediate: false,
        },
        { "x-access-token": user.token }
      );
      await fetchAll();
    } catch (err) {
      console.error(err);
    }
  };

  if (!isOpen || !targetUser) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl rounded-2xl shadow-xl p-4 md:p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            مدیریت اشتراک‌ها — {targetUser.name || targetUser.email}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
          >
            <X />
          </button>
        </div>

        {/* فرم اعطا/تمدید */}
        <form
          onSubmit={handleGrant}
          className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4"
        >
          <div>
            <label className="block text-sm mb-1">دسته‌بندی (L3)</label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.categoryId}
              onChange={(e) =>
                setForm((f) => ({ ...f, categoryId: e.target.value }))
              }
            >
              <option value="">انتخاب کنید…</option>
              {cats.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">پلن</label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.plan}
              onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
            >
              {["free", "basic", "pro"].map((p) => (
                <option key={p} value={p}>
                  {PLAN_LABELS[p]?.fa || p}
                </option>
              ))}
            </select>
          </div>

          <div
            className={`flex items-end ${
              isRtl ? "justify-start" : "justify-end"
            }`}
          >
            <button
              type="submit"
              disabled={creating || !form.categoryId}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Plus className="w-4 h-4" />
              اعطا/تمدید
            </button>
          </div>
        </form>

        {/* لیست اشتراک‌ها */}
        <div className="border rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-right py-2 px-3">دسته‌بندی</th>
                <th className="text-center py-2 px-3">پلن</th>
                <th className="text-center py-2 px-3">شروع</th>
                <th className="text-center py-2 px-3">پایان</th>
                <th className="text-center py-2 px-3">وضعیت</th>
                <th className="text-center py-2 px-3">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-gray-500">
                    در حال بارگذاری…
                  </td>
                </tr>
              ) : subs.length ? (
                subs.map((s) => {
                  const active =
                    (!s.canceledAt || new Date(s.canceledAt) > new Date()) &&
                    new Date(s.endAt) > new Date();
                  return (
                    <tr key={s._id} className="border-t">
                      <td className="py-2 px-3 text-right">
                        {s.category?.title || "-"}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {PLAN_LABELS[s.plan]?.fa || s.plan}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {s.startAt
                          ? new Date(s.startAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {s.endAt ? new Date(s.endAt).toLocaleDateString() : "-"}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            active
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {active ? "فعال" : "غیرفعال"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={() => handleCancel(s)}
                          className="px-3 py-1.5 rounded-md text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                          title="لغو (عدم تمدید از دوره بعد)"
                        >
                          <Trash2 className="w-4 h-4" />
                          لغو
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-gray-500">
                    اشتراکی ثبت نشده است
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={`mt-4 flex ${isRtl ? "justify-start" : "justify-end"}`}>
          <button
            className="px-4 py-2 rounded-lg border hover:bg-gray-50"
            onClick={onClose}
          >
            بستن
          </button>
        </div>
      </div>
    </div>
  );
}
